/**
 * QaTab.tsx
 *
 * Patient-detail Quality Assurance section matching legacy comm-log-qa.php.
 * Shows the patient-scoped list of QA cases; clicking a row opens the shared
 * QaCaseEditModal (same editor as the admin /qa page). A "New QA Case"
 * button above the list opens the modal in create mode with the patient
 * pre-filled.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { listQaItems, type QaItem, type QaStatus } from '@/services/qa.service';
import { Badge, Spinner, Button } from '@/components/ui';
import { QaCaseEditModal } from '@/components/qa/QaCaseEditModal';
import { useAuth } from '@/hooks/useAuth';
import { useRoleTitles } from '@/hooks/useRoleTitles';

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  // Legacy zero-date sentinel means "no date set" — same rule as the admin /qa page.
  if (value.startsWith('0000-00-00')) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Legacy status codes (0-3) from comm-log-qa.php.
const QA_STATUS_MAP: Record<QaStatus, { label: string; variant: 'neutral' | 'warning' | 'info' | 'success' | 'error' }> = {
  notInProcess:    { label: 'Not in process',    variant: 'neutral' },
  inProcess:       { label: 'In process',        variant: 'warning' },
  unableToResolve: { label: 'Unable to resolve', variant: 'error'   },
  resolved:        { label: 'Resolved',          variant: 'success' },
};

interface QaTabProps {
  patientId: number;
}

export function QaTab({ patientId }: QaTabProps) {
  const { hasPermission } = useAuth();
  const canSeeList = hasPermission(1);
  const canSubmit = hasPermission(24);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-qa', patientId],
    queryFn: () => listQaItems({ patientId, pageSize: 100 }),
    enabled: canSeeList,
  });

  // `editCaseId` drives the shared modal:
  //   undefined → closed.
  //   null      → create mode (patient prefilled).
  //   number    → edit that case.
  const [editCaseId, setEditCaseId] = useState<number | null | undefined>(undefined);
  const editOpen = editCaseId !== undefined;

  const rows: QaItem[] = data?.data ?? [];

  // Column header uses the account's own wording for the role.
  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Quality Assurance</h2>
        {canSubmit && (
          <Button size="sm" onClick={() => setEditCaseId(null)} leftIcon={<Plus className="h-4 w-4" />}>
            New QA Case
          </Button>
        )}
      </div>

      {canSeeList && (
        <>
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : isError ? (
            <p className="py-4 text-sm text-red-600">Failed to load QA items.</p>
          ) : rows.length === 0 ? (
            <p className="py-4 text-sm text-slate-400 italic">No QA items found for this patient.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">{doctorLabel}</th>
                    <th className="px-4 py-3">Date of Procedure</th>
                    <th className="px-4 py-3">Date Added</th>
                    <th className="px-4 py-3">Reasons</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Facility</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((item) => {
                    const key = (item.qaStatus ?? 'notInProcess') as QaStatus;
                    const statusInfo =
                      QA_STATUS_MAP[key] ?? { label: String(item.qaStatus ?? ''), variant: 'neutral' as const };
                    return (
                      <tr
                        key={item.patientsQaID}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => setEditCaseId(item.patientsQaID)}
                      >
                        <td className="px-4 py-3">
                          <Badge variant={statusInfo.variant} size="sm">{statusInfo.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{item.doctorName ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-700">{formatShortDate(item.procDate)}</td>
                        <td className="px-4 py-3 text-slate-700">{formatShortDate(item.addDate)}</td>
                        <td className="px-4 py-3 text-slate-500">
                          {item.reasons.length > 0
                            ? item.reasons.map((r) => r.title).join(', ')
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{item.planTypeName ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-700">{item.facilityName ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!canSeeList && (
        <p className="py-4 text-sm text-slate-500">
          You don't have permission to view Q/A cases. Ask an admin to grant the Q/A Visibility permission.
        </p>
      )}

      <QaCaseEditModal
        caseId={typeof editCaseId === 'number' ? editCaseId : null}
        patientId={patientId}
        open={editOpen}
        onOpenChange={(o) => setEditCaseId(o ? editCaseId : undefined)}
      />
    </div>
  );
}
